import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Save, Plus, Trash2, RotateCcw, FileText, Info,
} from 'lucide-react';

const SECTION_TYPES = [
  { id: 'select', label: 'Auswahl (mehrere, ohne Seriennummer)' },
  { id: 'select_serial', label: 'Auswahl + Seriennummer' },
  { id: 'count_serial_list', label: 'Freie Zeilen (Bezeichnung/Anzahl/Seriennummer)' },
  { id: 'textarea', label: 'Freitext / Notiz' },
];

export default function DocumentTemplateTab() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // 04.02.2026: hardcoded Rollen-Liste ['admin','technik','innendienst','support']
  // durch Matrix-Aktion ersetzt. Wer `order_documents.template_edit` in der
  // Aktionsmatrix hat, darf die Vorlage editieren — jede Rolle, nicht nur die
  // fest verdrahteten.
  const canEdit = can('order_documents.template_edit');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch('/api/order-documents/template');
    if (r.ok) setTpl(await r.json());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg('');
    const r = await authFetch('/api/order-documents/template', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: tpl.title, header: tpl.header, sections: tpl.sections }),
    });
    if (r.ok) { setMsg('✓ Vorlage gespeichert'); setTimeout(() => setMsg(''), 2500); }
    else { const d = await r.json().catch(() => ({})); setMsg(d.detail || 'Fehler'); }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm('Vorlage wirklich auf Werkszustand zurücksetzen?')) return;
    const r = await authFetch('/api/order-documents/template/reset', { method: 'POST' });
    if (r.ok) { setMsg('✓ Auf Werkszustand zurückgesetzt'); await load(); setTimeout(() => setMsg(''), 2500); }
  };

  const addHeader = () => setTpl(t => ({ ...t, header: [...(t.header || []), { key: 'neu' + Date.now(), label: 'Neues Feld', type: 'text' }] }));
  const updHeader = (i, patch) => setTpl(t => ({ ...t, header: t.header.map((h, j) => i === j ? { ...h, ...patch } : h) }));
  const delHeader = (i) => setTpl(t => ({ ...t, header: t.header.filter((_, j) => j !== i) }));

  const addSection = () => setTpl(t => ({ ...t, sections: [...(t.sections || []), { id: 'sec' + Date.now(), label: 'Neue Sektion', type: 'select_serial', options: [], allowCustom: true }] }));
  const updSection = (i, patch) => setTpl(t => ({ ...t, sections: t.sections.map((s, j) => i === j ? { ...s, ...patch } : s) }));
  const delSection = (i) => { if (!window.confirm('Sektion löschen?')) return; setTpl(t => ({ ...t, sections: t.sections.filter((_, j) => j !== i) })); };

  const moveSection = (i, dir) => setTpl(t => {
    const arr = [...t.sections];
    const to = i + dir;
    if (to < 0 || to >= arr.length) return t;
    [arr[i], arr[to]] = [arr[to], arr[i]];
    return { ...t, sections: arr };
  });

  if (loading || !tpl) return <div className="text-xs text-gray-400">Lade Vorlage…</div>;

  return (
    <div className="max-w-4xl" data-testid="document-template-tab">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#9ec043]" />
        <h2 className="text-sm font-semibold text-[#32373c]">Produktbegleitdokument — Vorlage</h2>
      </div>
      <p className="text-xs text-gray-400 mb-4 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 inline-flex items-center gap-2">
        <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
        Die Vorlage gilt für alle Aufträge. Pro Auftrag wird sie automatisch mit den Komponenten des verkauften Systems vorbefüllt — zusätzliche Felder für Sonderversionen lassen sich beim Bearbeiten ergänzen.
      </p>
      <div className="flex items-center gap-2 mb-5">
        {canEdit && (
          <>
            <Button onClick={save} disabled={saving} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-save-template">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Speichern…' : 'Speichern'}
            </Button>
            {user?.role === 'admin' && (
              <Button variant="outline" onClick={reset} className="text-sm" data-testid="btn-reset-template">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Zurücksetzen
              </Button>
            )}
          </>
        )}
        {msg && <span className={`text-xs ${msg.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`} data-testid="template-msg">{msg}</span>}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <label className="text-[10px] uppercase tracking-wider text-gray-400">Titel</label>
        <Input value={tpl.title || ''} onChange={e => setTpl(t => ({ ...t, title: e.target.value }))}
          className="mt-0.5 text-base font-semibold" disabled={!canEdit} data-testid="template-title" />
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-semibold text-[#32373c]">Kopfdaten</h3>
          {canEdit && <Button variant="outline" onClick={addHeader} className="text-xs h-7" data-testid="btn-add-header"><Plus className="w-3 h-3 mr-1" /> Feld hinzufügen</Button>}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400">
            <tr><th className="text-left px-3 py-1.5">Label</th>
                <th className="text-left px-3 py-1.5 w-32">Key</th>
                <th className="text-left px-3 py-1.5 w-32">Typ</th>
                {canEdit && <th className="w-10" />}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(tpl.header || []).map((h, i) => (
              <tr key={i} data-testid={`template-header-row-${i}`}>
                <td className="px-3 py-1.5"><Input value={h.label || ''} onChange={e => updHeader(i, { label: e.target.value })} className="text-sm h-7 border-gray-200" disabled={!canEdit} /></td>
                <td className="px-3 py-1.5"><Input value={h.key || ''} onChange={e => updHeader(i, { key: e.target.value })} className="text-sm h-7 border-gray-200 font-mono" disabled={!canEdit} /></td>
                <td className="px-3 py-1.5">
                  <select value={h.type || 'text'} onChange={e => updHeader(i, { type: e.target.value })} className="text-sm h-7 border border-gray-200 rounded px-2 w-full" disabled={!canEdit}>
                    <option value="text">Text</option><option value="date">Datum</option>
                  </select>
                </td>
                {canEdit && <td className="px-1"><button onClick={() => delHeader(i)} className="p-1.5 text-gray-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sections */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-sm font-semibold text-[#32373c]">Sektionen</h3>
          {canEdit && <Button variant="outline" onClick={addSection} className="text-xs h-7" data-testid="btn-add-section"><Plus className="w-3 h-3 mr-1" /> Sektion hinzufügen</Button>}
        </div>
        <div className="divide-y divide-gray-100">
          {(tpl.sections || []).map((s, i) => (
            <div key={i} className="p-3 space-y-2" data-testid={`template-section-${i}`}>
              <div className="flex items-center gap-2">
                <div className="flex flex-col flex-1 gap-0.5">
                  <Input value={s.label || ''} onChange={e => updSection(i, { label: e.target.value })}
                    className="text-sm h-8 border-gray-200 font-semibold" disabled={!canEdit}
                    data-testid={`template-section-label-${i}`} />
                  {/* Technische ID als Hilfsanzeige — wichtig um Duplikat-
                      Sektionen mit gleichem Label aber unterschiedlicher
                      ID (z. B. `energyMeterIntern` vs. eine manuell um-
                      benannte alte Sektion) auseinanderhalten zu können. */}
                  <span className="text-[10px] text-gray-400 font-mono pl-1">id: {s.id || '(neu)'}</span>
                </div>
                <select value={s.type || 'select_serial'} onChange={e => updSection(i, { type: e.target.value })}
                  className="text-sm h-8 border border-gray-200 rounded px-2 w-72" disabled={!canEdit}
                  data-testid={`template-section-type-${i}`}>
                  {SECTION_TYPES.map(st => <option key={st.id} value={st.id}>{st.label}</option>)}
                </select>
                {canEdit && (
                  <>
                    <button onClick={() => moveSection(i, -1)} className="text-xs text-gray-400 hover:text-gray-600 px-1">↑</button>
                    <button onClick={() => moveSection(i, +1)} className="text-xs text-gray-400 hover:text-gray-600 px-1">↓</button>
                    <button onClick={() => delSection(i)} className="p-1.5 text-gray-300 hover:text-red-500" data-testid={`btn-del-section-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
              {(s.type === 'select' || s.type === 'select_serial') && (
                <div className="pl-2 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">Optionen (eine pro Zeile)</div>
                  <textarea
                    value={(s.options || []).join('\n')}
                    onChange={e => updSection(i, { options: e.target.value.split('\n').map(x => x.trim()).filter(Boolean) })}
                    className="w-full border border-gray-200 rounded p-2 text-sm font-mono min-h-16"
                    rows={Math.max(3, (s.options || []).length + 1)}
                    disabled={!canEdit}
                    data-testid={`template-section-options-${i}`}
                  />
                  {s.type === 'select_serial' && (
                    <label className="text-xs text-gray-500 inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={!!s.allowCustom}
                        onChange={e => updSection(i, { allowCustom: e.target.checked })}
                        className="accent-[#9ec043]" disabled={!canEdit} />
                      „Sonderversion hinzufügen"-Button im Editor erlauben
                    </label>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
